import React, { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { RefreshCw } from 'lucide-react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { DomainDashboard } from "@/components/domains/DomainDashboard"
import { MailboxDashboard } from "@/components/mailbox-management/MailboxDashboard"
import { useMailboxTwoWaySync } from "@/hooks/useMailboxTwoWaySync"
import { useBackgroundSyncScheduler } from "@/lib/mailbox-sync/background-sync-scheduler"


export function DomainsAndMailboxDashboard() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [activeTab, setActiveTab] = useState("domains")

  // Two-way sync with Smartlead. 2026-06-11 (86ca7n8d4): the BACKGROUND
  // scheduler is fully retired (see background-sync-scheduler.ts) — the
  // daily-mailbox-status-sync edge function owns SmartLead state. Only the
  // explicit manual Sync button below (performSync) remains, per Sabeeh.
  const { performSync, isSyncing, progress, stats } = useMailboxTwoWaySync('smartlead')
  const { isRunning: isBackgroundSyncRunning, status: schedulerStatus } = useBackgroundSyncScheduler()

  useEffect(() => {
    const section = searchParams.get('section')
    if (section === 'mailboxes') {
      setActiveTab('mailboxes')
    } else if (section === 'domains') {
      setActiveTab('domains')
    }
  }, [searchParams])

  const handleTabChange = (tab: string) => {
    setActiveTab(tab)
    // Update URL to reflect tab change while preserving other params
    const newParams = new URLSearchParams(searchParams)
    newParams.set('section', tab)
    setSearchParams(newParams)
  }

  return (
    <div className="w-full max-w-none">
      <div className="p-6 space-y-8">
        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-8">
            <TabsTrigger value="domains">Domains</TabsTrigger>
            <TabsTrigger value="mailboxes">Mailboxes</TabsTrigger>
          </TabsList>
          
          <TabsContent value="domains" className="mt-0">
            <div className="space-y-8">
              <div className="flex items-center justify-between">
                <div className="border-l-4 border-blue-500 pl-6 py-2">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-2">Domain Management</h2>
                  <p className="text-gray-600">Monitor and manage your email domains</p>
                </div>
                <Button variant="outline" onClick={() => window.location.href = window.location.pathname + window.location.search} className="shadow-sm">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </div>
              <DomainDashboard />
            </div>
          </TabsContent>
          
          <TabsContent value="mailboxes" className="mt-0">
            <div className="space-y-8">
              <div className="flex items-center justify-between">
                <div className="border-l-4 border-green-500 pl-6 py-2">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-2">Mailbox Management</h2>
                  <p className="text-gray-600">Monitor and manage your email mailboxes</p>
                </div>
                <Button variant="outline" onClick={() => window.location.href = window.location.pathname + window.location.search} className="shadow-sm">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </div>
              
              
              <div className="space-y-8">
                <MailboxDashboard 
                  performSync={performSync}
                  isSyncing={isSyncing}
                  progress={progress}
                  stats={stats}
                  isBackgroundSyncRunning={isBackgroundSyncRunning}
                  schedulerStatus={schedulerStatus}
                />
              </div>
            </div>
          </TabsContent>
          
        </Tabs>
      </div>
    </div>
  )
}